import { useState, useRef } from 'react';
import { useStore } from '@/store';
import type { StudyArm } from '@bioavail/shared-types';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { parseCsv, inferColumnMapping, mapColumns, type ColumnMapping } from '@/features/import/csvParser';

const PREVIEW_ROWS = 10;

/** CSV/TSV import dialog with delimiter detection + column mapping (F-024). */
export function CsvImportDialog({
  open,
  onClose,
  arm,
}: {
  open: boolean;
  onClose: () => void;
  arm: StudyArm;
}) {
  const importCsv = useStore((s) => s.importCsv);
  const pushToast = useStore((s) => s.pushToast);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [parsed, setParsed] = useState<ReturnType<typeof parseCsv> | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function handleFile(file: File) {
    setFileName(file.name);
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      try {
        const result = parseCsv(text);
        const inferred = inferColumnMapping(result.headers);
        setParsed(result);
        setMapping(inferred);
      } catch (err) {
        setParsed(null);
        setMapping(null);
        setError(err instanceof Error ? err.message : 'Could not parse file.');
      }
    };
    reader.onerror = () => setError('Could not read file.');
    reader.readAsText(file);
  }

  function handleConfirm() {
    if (!parsed || !mapping) return;
    try {
      const mapped = mapColumns(parsed, mapping);
      const points = mapped.time.map((t, i) => ({
        time: t,
        concentration: mapped.concentration[i]!,
        isBlq: mapped.isBlq[i] ?? false,
      }));
      importCsv(arm.id, points);
      pushToast(`Imported ${points.length} data points into "${arm.label}".`, 'info');
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not parse file.');
    }
  }

  function handleClose() {
    setFileName('');
    setError(null);
    setMapping(null);
    setParsed(null);
    onClose();
  }

  const previewRows = parsed ? parsed.rows.slice(0, PREVIEW_ROWS) : [];

  return (
    <Modal
      open={open}
      title={`Import CSV — ${arm.label}`}
      onClose={handleClose}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!parsed || !!error}>
            Confirm
          </Button>
        </>
      }
    >
      <div className="flex-col-gap-3">
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.tsv,text/csv,text/tab-separated-values"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
          {fileName ? <p className="field-hint">Loaded: {fileName}</p> : null}
        </div>

        {parsed ? (
          <p className="field-hint">
            Detected delimiter: <code>{parsed.delimiter === '\t' ? 'tab' : parsed.delimiter}</code> ·{' '}
            {parsed.rows.length} rows
          </p>
        ) : null}

        {mapping && parsed ? (
          <div className="flex-row-gap-2">
            <Select
              label="Time column"
              value={String(mapping.timeColumn)}
              onChange={(v) => setMapping({ ...mapping, timeColumn: Number(v) })}
              options={parsed.headers.map((h, i) => ({ value: String(i), label: h }))}
            />
            <Select
              label="Concentration column"
              value={String(mapping.concentrationColumn)}
              onChange={(v) =>
                setMapping({ ...mapping, concentrationColumn: Number(v) })
              }
              options={parsed.headers.map((h, i) => ({ value: String(i), label: h }))}
            />
          </div>
        ) : null}

        {previewRows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="generic-table">
              <thead>
                <tr>
                  {parsed!.headers.map((h, i) => (
                    <th key={i}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {error ? (
          <p className="field-error" role="alert">
            Could not parse file: {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
